import maplibregl, { type LngLatBounds, type MapMouseEvent } from "maplibre-gl";
import { type Dispatch, type SetStateAction, useEffect, useRef } from "react";
import ReactDOMServer from "react-dom/server";
import { VEHICLE_CUSTOM_LAYER_ID } from "../components/MapLibre/mapLayerIds";
import { buildItineraryTransitPathStopCollisionEntries } from "../components/MapLibre/transitPath/itineraryTransitPath";
import { ensureItineraryLegLineLayer } from "../components/MapLibre/transitPath/routeLines";
import { TransitPathStopCollisionController } from "../components/MapLibre/transitPath/stopOverlay";
import TransportModeBadgeIcon from "../components/ItinerariesPlanner/shared/TransportModeBadgeIcon";
import HeLemanBadge from "../image/icons/map/transportBadges/HeLemanBadge.svg";
import BicycleIcon from "../image/icons/map/transportIcons/BicycleIcon.svg";
import BlaBlaCarIcon from "../image/icons/map/transportIcons/BlaBlaCarIcon.svg";
import BlaBlaDailyIcon from "../image/icons/map/transportIcons/BlaBlaDailyIcon.svg";
import BusIcon from "../image/icons/map/transportIcons/BusIcon.svg";
import CargoBicycleIcon from "../image/icons/map/transportIcons/CargoBicycleIcon.svg";
import CarIcon from "../image/icons/map/transportIcons/CarIcon.svg";
import MopedIcon from "../image/icons/map/transportIcons/MopedIcon.svg";
import ScooterSeatedIcon from "../image/icons/map/transportIcons/ScooterSeatedIcon.svg";
import ScooterStandingIcon from "../image/icons/map/transportIcons/ScooterStandingIcon.svg";
import SharingBikeIcon from "../image/icons/map/transportIcons/SharingBikeIcon3.svg";
import TrainIcon from "../image/icons/map/transportIcons/TrainIcon.svg";
import TramIcon from "../image/icons/map/transportIcons/TramIcon.svg";
import WalkIcon from "../image/icons/map/transportIcons/WalkingIcon.svg";
import { useStopLinesReady } from "../services/StopLinesService";
import { drawnItineraryGeometrySlice, ephemeralStore, itineraryPanelStateSlice, mapReadySlice, useEphemeralSelector, useEphemeralSliceSetter, useMapInstance } from "../store/ephemeral";
import {
    logAppMemoryReport,
    recordActiveItineraryForMemoryProfiling,
    recordItineraryMapMemoryStats,
} from "../tools/AppMemoryProfiler.tools";
import { resolveBlackLikeHexToVehicleRouteGreen } from "../tools/ColorTools";
import { DecodePath } from "../tools/ItineraryDecoder";
import {
    ensureItineraryWalkDotMapImage,
    ITINERARY_WALK_DOT_IMAGE_ID,
} from "../tools/MapItineraryTools";
import { estimateJsonUtf8Bytes } from "../tools/MemoryEstimate.tools";
import { getItineraryLegColorHex } from "../tools/TransportModeColorsTools";
import {
    getRentalLegColorMode,
    getRentalVehicleDisplayGroup,
    getTransportModeGroupForItineraryLeg,
    mapTransportModeToRouteType,
    RentalVehicleDisplayGroup,
    TransportModeGroup,
} from "../tools/TransportModeGroupsTools";
import { type ItineraryDTO, type ItineraryLegDTO } from "../types/dtos/api/itinerary/itinerary.dto";

const LINE_WIDTH_DEFAULT = 5;
const LINE_WIDTH_SELECTED = 10;
const LINE_OPACITY_DIMMED = 0.38;
const LINE_OPACITY_DEFAULT = 1;
const CIRCLE_RADIUS_DEFAULT = 8;
const CIRCLE_RADIUS_SELECTED = 11;

/** Screen-space spacing between walk dots along the leg geometry. */
const WALK_DOT_SYMBOL_SPACING = 15;

/** Default / selected icon scale for walk dot symbols (relative to sprite size). */
const WALK_DOT_ICON_SIZE_DEFAULT = 0.7;
const WALK_DOT_ICON_SIZE_SELECTED = 1;

interface LegMapLayers {
    "lineLayerId":      string;
    "lineSourceId":     string;
    "legEndLayerId":    string;
    "legEndSourceId":   string;
    "polylineColor":    string;
    "leg":              ItineraryLegDTO;
    "walkDotsLayerId"?: string;
}

/**
 * Returns map layer ids for one leg in bottom-to-top paint order.
 *
 * @param entry Leg layer registry entry.
 * @returns Layer ids ordered from bottom to top for that leg.
 */
function getLegLayerIdsBottomToTop( entry: LegMapLayers ): string[] {
    const layerIds = [ entry.lineLayerId ];

    if ( entry.walkDotsLayerId !== undefined ) {
        layerIds.push( entry.walkDotsLayerId );
    }

    layerIds.push( entry.legEndLayerId );

    return layerIds;
}

/**
 * Reorders itinerary leg layers so walk legs stay above other modes, except the
 * currently highlighted leg which is always painted on top.
 *
 * @param map Active MapLibre map instance.
 * @param layersMap Registry of drawn leg layers keyed by leg id.
 * @param selectedLegId Highlighted leg id, if any.
 */
function applyItineraryLegLayerZOrder(
    map: maplibregl.Map,
    layersMap: Map<string, LegMapLayers>,
    selectedLegId: string | undefined,
): void {
    if ( layersMap.size === 0 ) {
        return;
    }

    const entries = Array.from( layersMap.values() );
    const selectedEntry = selectedLegId !== undefined
        ? entries.find( ( entry ) => entry.leg.id === selectedLegId )
        : undefined;
    const nonSelectedEntries = entries.filter( ( entry ) => entry.leg.id !== selectedLegId );

    const nonWalkEntries = nonSelectedEntries.filter( ( entry ) => getTransportModeGroupForItineraryLeg( entry.leg ) !== TransportModeGroup.WALK );
    const walkEntries = nonSelectedEntries.filter( ( entry ) => getTransportModeGroupForItineraryLeg( entry.leg ) === TransportModeGroup.WALK );

    const legsBottomToTop: LegMapLayers[] = selectedEntry === undefined
        ? [ ...nonWalkEntries, ...walkEntries ]
        : [ ...nonWalkEntries, ...walkEntries, selectedEntry ];

    const layerIdsBottomToTop = legsBottomToTop.flatMap( getLegLayerIdsBottomToTop );
    const topBeforeId = map.getLayer( VEHICLE_CUSTOM_LAYER_ID ) != null ? VEHICLE_CUSTOM_LAYER_ID : undefined;

    layerIdsBottomToTop.forEach( ( layerId ) => {
        if ( map.getLayer( layerId ) != null ) {
            map.moveLayer( layerId, topBeforeId );
        }
    } );
}

/**
 * Returns the JSX icon element used for leg start markers.
 *
 * @param leg Itinerary leg used for mode grouping and rental icon selection.
 * @returns JSX icon element for marker rendering or null when unsupported.
 */
function getModeIconJSX( leg: ItineraryLegDTO ) {
    const { mode } = leg;

    switch ( getTransportModeGroupForItineraryLeg( leg ) ) {
        case TransportModeGroup.MY_CAR: // Fallthrough
            return ( <TransportModeBadgeIcon
                alt="Car"
                badgeSizePx={30}
                iconSizePx={16}
                iconSrc={CarIcon}
                mode={mode}
                     /> );

        case TransportModeGroup.CAR:
            return ( <TransportModeBadgeIcon
                alt="Car"
                badgeSizePx={30}
                iconSizePx={16}
                iconSrc={CarIcon}
                mode={mode}
                     /> );

        case TransportModeGroup.TRAIN:
            return ( <TransportModeBadgeIcon
                alt="Train"
                badgeSizePx={30}
                iconSizePx={16}
                iconSrc={TrainIcon}
                mode={mode}
                     /> );

        case TransportModeGroup.TRAM:
            return ( <TransportModeBadgeIcon
                alt="Tram"
                badgeSizePx={30}
                iconSizePx={16}
                iconSrc={TramIcon}
                mode={mode}
                     /> );

        case TransportModeGroup.BUS:
            return ( <TransportModeBadgeIcon
                alt="Bus"
                badgeSizePx={30}
                iconSizePx={16}
                iconSrc={BusIcon}
                mode={mode}
                     /> );

        case TransportModeGroup.WALK:
            return ( <TransportModeBadgeIcon
                alt="Walk"
                badgeSizePx={30}
                iconSizePx={16}
                iconSrc={WalkIcon}
                mode={mode}
                     /> );

        case TransportModeGroup.BICYCLE:
            return ( <TransportModeBadgeIcon
                alt="Bicycle"
                badgeSizePx={30}
                iconSizePx={16}
                iconSrc={BicycleIcon}
                mode={mode}
                     /> );

        case TransportModeGroup.RENTAL:
        {
            const rentalVehicleType = leg.rental?.vehicle?.type;
            const rentalDisplayGroup = getRentalVehicleDisplayGroup( rentalVehicleType );
            let rentalIconSrc = SharingBikeIcon;
            let rentalAlt = "SharingBike";

            switch ( rentalDisplayGroup ) {
                case RentalVehicleDisplayGroup.CARGO_BICYCLE:
                    rentalIconSrc = CargoBicycleIcon;
                    rentalAlt = "CargoBicycle";
                    break;

                case RentalVehicleDisplayGroup.SCOOTER_STANDING:
                    rentalIconSrc = ScooterStandingIcon;
                    rentalAlt = "ScooterStanding";
                    break;

                case RentalVehicleDisplayGroup.SCOOTER_SEATED:
                    rentalIconSrc = ScooterSeatedIcon;
                    rentalAlt = "ScooterSeated";
                    break;

                case RentalVehicleDisplayGroup.CAR:
                    rentalIconSrc = CarIcon;
                    rentalAlt = "SharingCar";
                    break;

                case RentalVehicleDisplayGroup.MOPED:
                    rentalIconSrc = MopedIcon;
                    rentalAlt = "SharingMoped";
                    break;

                default:
                    break;
            }

            return ( <TransportModeBadgeIcon
                alt={rentalAlt}
                badgeSizePx={30}
                iconSizePx={18}
                iconSrc={rentalIconSrc}
                mode={getRentalLegColorMode( leg )}
                     /> );
        }

        case TransportModeGroup.BLABLADAILY:
            return ( <TransportModeBadgeIcon
                alt="BlaBlaDaily"
                badgeSizePx={30}
                iconSizePx={16}
                iconSrc={BlaBlaDailyIcon}
                mode={mode}
                     /> );

        case TransportModeGroup.BLABLACAR:
            return ( <TransportModeBadgeIcon
                alt="BlablaCar"
                badgeSizePx={30}
                iconSizePx={16}
                iconSrc={BlaBlaCarIcon}
                mode={mode}
                     /> );

        case TransportModeGroup.HE_LEMAN:
            return ( <img
                alt="HeLeman"
                className="w-[20px]"
                src={HeLemanBadge}
                     /> );

        case TransportModeGroup.UNSUPPORTED_LIFT:
            return null;

        default:
            return null;
    }
}


interface UseMapItineraryProps {
    "itinerary":             ItineraryDTO | null;
    "itineraryListHeight":   number;
    "selectedLeg":           ItineraryLegDTO | null;
    "setSelectedLeg":        Dispatch<SetStateAction<ItineraryLegDTO | null>>;
    "isDarkUi":              boolean;
    "onLegSelectedFromMap"?: () => void;
}

/**
 * Draws itinerary legs on MapLibre and keeps selection styling/labels in sync.
 *
 * Key design:
 * - Effect A draws map layers/markers and installs map interactions.
 * - Effect B only updates selection style + stop labels to avoid redraw flicker.
 *
 * @param props Hook input values for itinerary rendering and selection interactions.
 * @returns No return value; map updates are applied through side effects.
 */
export function useMapItinerary( {
    itinerary,
    itineraryListHeight,
    selectedLeg,
    setSelectedLeg,
    isDarkUi,
    onLegSelectedFromMap,
}: UseMapItineraryProps ): void {
    const map = useMapInstance();
    const isMapReady = useEphemeralSelector( mapReadySlice, ( ready ) => ready.isMapReady );
    const isStopLinesReady = useStopLinesReady();
    const isDragging = useEphemeralSelector( itineraryPanelStateSlice, ( panel ) => panel.isDragging );
    const setDrawnItineraryGeometry = useEphemeralSliceSetter( drawnItineraryGeometrySlice );

    const mapElementsRef = useRef<
        Array<{
            "sourceId"?: string;
            "layerId"?:  string;
            "marker"?:   maplibregl.Marker;
        }>
    >( [] );

    const legLayersByIdRef = useRef<Map<string, LegMapLayers>>( new Map() );
    const stopLabelControllerRef = useRef<TransitPathStopCollisionController | null>( null );
    const itineraryRef = useRef<ItineraryDTO | null>( itinerary );
    const selectedLegRef = useRef( selectedLeg );
    const setSelectedLegRef = useRef( setSelectedLeg );
    const onLegSelectedFromMapRef = useRef( onLegSelectedFromMap );
    const handleMapClickRef = useRef<( ( event: MapMouseEvent ) => void ) | null>( null );
    const handleMapMouseMoveRef = useRef<( ( event: MapMouseEvent ) => void ) | null>( null );

    // Prevent auto-fit loops when style/layer reload re-triggers drawing.
    const lastAutoFitKeyRef = useRef<string | null>( null );

    useEffect( () => {
        itineraryRef.current = itinerary;
        selectedLegRef.current = selectedLeg;
        setSelectedLegRef.current = setSelectedLeg;
        onLegSelectedFromMapRef.current = onLegSelectedFromMap;
    }, [ itinerary, selectedLeg, setSelectedLeg, onLegSelectedFromMap ] );

    /*
     * Effect A — create map objects and interaction handlers.
     * Keep `selectedLeg` out of dependencies so a selection click does not rebuild all layers.
     * While `isDragging`, skip redraw; cleanup also skips teardown so layers stay on the map.
     */
    useEffect( () => {
        if ( map === undefined || !isMapReady || itinerary == null ) {
            legLayersByIdRef.current = new Map();

            return;
        }

        if ( isDragging ) {
            return;
        }

        const containerHeight = ( itineraryListHeight * window.innerHeight ) / 100;
        const topContainerHeight = 160;
        const bottomContainerHeight = containerHeight;
        const itineraryFitKey = `${ itinerary.legs.map( ( leg ) => `${ leg.id }::${ leg.geometry?.path ?? "" }` ).join( "|" ) }::${ Math.round( itineraryListHeight ) }`;

        const clearMapElements = () => {
            // Always remove in reverse ownership order: layer -> source -> marker.
            mapElementsRef.current.forEach( ( elem ) => {
                if ( elem.layerId !== undefined && map.getLayer( elem.layerId ) != null ) {
                    map.removeLayer( elem.layerId );
                }
                if ( elem.sourceId !== undefined && map.getSource( elem.sourceId ) != null ) {
                    map.removeSource( elem.sourceId );
                }
                if ( elem.marker != null ) {
                    elem.marker.remove();
                }
            } );
            mapElementsRef.current = [];
        };

        const resolveQueryableLineLayerIds = (): string[] => {
            return Array.from( legLayersByIdRef.current.values() ).
                map( ( entry ) => entry.lineLayerId ).
                filter( ( layerId ) => map.getLayer( layerId ) != null );
        };

        const removeMapInteractionHandlers = () => {
            if ( handleMapClickRef.current !== null ) {
                map.off( "click", handleMapClickRef.current );
                handleMapClickRef.current = null;
            }

            if ( handleMapMouseMoveRef.current !== null ) {
                map.off( "mousemove", handleMapMouseMoveRef.current );
                handleMapMouseMoveRef.current = null;
            }

            map.getCanvas().style.cursor = "";
        };

        const addItineraryToMap = () => {
            removeMapInteractionHandlers();
            clearMapElements();
            legLayersByIdRef.current = new Map();

            let bounds: LngLatBounds | null = null;

            ensureItineraryWalkDotMapImage( map );

            itinerary.legs.forEach( ( leg: ItineraryLegDTO, index: number ) => {
                if ( leg.geometry?.path === undefined || leg.mode === undefined ) {
                    return;
                }

                const decodedPath = DecodePath( leg.geometry.path, leg.geometry.type, leg.geometry.precision );
                if ( decodedPath.length === 0 ) {
                    return;
                }

                const polylineColor = resolveBlackLikeHexToVehicleRouteGreen( getItineraryLegColorHex( leg ) );
                const isWalkLeg = getTransportModeGroupForItineraryLeg( leg ) === TransportModeGroup.WALK;

                // Date-based suffix prevents source/layer id collisions on fast redraws.
                const suffix = `${ leg.id }_${ index }_${ Date.now() }`;
                const lineSourceId = `lineSource_${ suffix }`;
                const lineLayerId = `lineLayer_${ suffix }`;
                const walkDotsLayerId = isWalkLeg ? `walkDotsLayer_${ suffix }` : undefined;

                const geojsonLine: GeoJSON.Feature = {
                    "type":     "Feature",
                    "geometry": {
                        "type":        "LineString",
                        "coordinates": decodedPath.map( ( latlng ) => [ latlng.lng, latlng.lat ] ),
                    },
                    "properties": { "legId": leg.id },
                };

                ensureItineraryLegLineLayer(
                    map,
                    lineSourceId,
                    lineLayerId,
                    geojsonLine,
                    polylineColor,
                    LINE_WIDTH_DEFAULT,
                    isWalkLeg ? 0 : LINE_OPACITY_DEFAULT,
                );

                const beforeId = map.getLayer( VEHICLE_CUSTOM_LAYER_ID ) != null ? VEHICLE_CUSTOM_LAYER_ID : undefined;

                if ( walkDotsLayerId !== undefined ) {
                    map.addLayer( {
                        "id":     walkDotsLayerId,
                        "type":   "symbol",
                        "source": lineSourceId,
                        "layout": {
                            "symbol-placement":      "line",
                            "symbol-spacing":        WALK_DOT_SYMBOL_SPACING,
                            "icon-image":            ITINERARY_WALK_DOT_IMAGE_ID,
                            "icon-size":             WALK_DOT_ICON_SIZE_DEFAULT,
                            "icon-allow-overlap":    true,
                            "icon-ignore-placement": true,
                        },
                        "paint": {
                            "icon-color":   polylineColor,
                            "icon-opacity": LINE_OPACITY_DEFAULT,
                        },
                    }, beforeId );
                }

                const endVertex = decodedPath[decodedPath.length - 1];
                const legEndSourceId = `legEndSource_${ suffix }`;
                const legEndLayerId = `legEndLayer_${ suffix }`;
                const legEndGeojson: GeoJSON.Feature = {
                    "type":     "Feature",
                    "geometry": {
                        "type":        "Point",
                        "coordinates": [ endVertex.lng, endVertex.lat ],
                    },
                    "properties": { "legId": leg.id },
                };

                map.addSource( legEndSourceId, {
                    "type": "geojson",
                    "data": legEndGeojson,
                } );

                map.addLayer( {
                    "id":     legEndLayerId,
                    "type":   "circle",
                    "source": legEndSourceId,
                    "paint":  {
                        "circle-radius":       CIRCLE_RADIUS_DEFAULT,
                        "circle-color":        polylineColor,
                        "circle-stroke-color": "#ffffff",
                        "circle-stroke-width": 2,
                        "circle-opacity":      1,
                    },
                }, beforeId );

                if ( walkDotsLayerId !== undefined ) {
                    mapElementsRef.current.push( { "layerId": walkDotsLayerId } );
                }

                mapElementsRef.current.push( {
                    "sourceId": lineSourceId,
                    "layerId":  lineLayerId,
                } );
                mapElementsRef.current.push( {
                    "sourceId": legEndSourceId,
                    "layerId":  legEndLayerId,
                } );

                legLayersByIdRef.current.set( leg.id, {
                    lineLayerId,
                    lineSourceId,
                    legEndLayerId,
                    legEndSourceId,
                    polylineColor,
                    leg,
                    walkDotsLayerId,
                } );

                // Build a global bounds object incrementally for one final fitBounds call.
                decodedPath.forEach( ( point ) => {
                    if ( bounds == null ) {
                        bounds = new maplibregl.LngLatBounds(
                            [ point.lng, point.lat ],
                            [ point.lng, point.lat ],
                        );
                    } else {
                        bounds.extend( [ point.lng, point.lat ] );
                    }
                } );

                const startMarkerHtml = ReactDOMServer.renderToStaticMarkup( getModeIconJSX( leg ) );
                const startMarkerEl = document.createElement( "div" );
                startMarkerEl.innerHTML = startMarkerHtml;
                startMarkerEl.style.cursor = "pointer";
                startMarkerEl.tabIndex = 0;
                startMarkerEl.setAttribute( "role", "button" );
                startMarkerEl.setAttribute( "aria-label", "Sélectionner la leg" );

                const selectLegFromStartMarker = ( event: Event ): void => {
                    // Prevent the global map click handler from firing after marker click.
                    event.preventDefault();
                    event.stopPropagation();

                    // Toggle behavior: clicking the same leg start marker unselects it.
                    setSelectedLegRef.current( ( previousLeg ) => {
                        if ( previousLeg !== null && previousLeg.id === leg.id ) {
                            return null;
                        }

                        return leg;
                    } );
                    onLegSelectedFromMapRef.current?.();
                };

                startMarkerEl.addEventListener( "click", ( event ) => {
                    selectLegFromStartMarker( event );
                } );
                startMarkerEl.addEventListener( "keydown", ( event: KeyboardEvent ) => {
                    if ( event.key === "Enter" || event.key === " " ) {
                        selectLegFromStartMarker( event );
                    }
                } );

                const startMarker = new maplibregl.Marker( { "element": startMarkerEl } ).
                    setLngLat( [ decodedPath[0].lng, decodedPath[0].lat ] ).
                    addTo( map );
                mapElementsRef.current.push( { "marker": startMarker } );
            } );

            if ( legLayersByIdRef.current.size > 0 ) {
                const handleMapClick = ( event: MapMouseEvent ): void => {
                    const lineLayerIds = resolveQueryableLineLayerIds();
                    if ( lineLayerIds.length === 0 ) {
                        return;
                    }

                    // Restrict feature lookup to itinerary leg layers to avoid POI/other layers interference.
                    const features = map.queryRenderedFeatures( event.point, { "layers": lineLayerIds } );
                    if ( features.length > 0 ) {
                        const legId = features[0].properties?.legId as string | undefined;
                        if ( legId !== undefined ) {
                            const found = itineraryRef.current?.legs.find( ( legItem ) => legItem.id === legId );
                            if ( found !== undefined ) {
                                // Toggle behavior on line click too (consistent with panel interactions).
                                setSelectedLegRef.current( ( previousLeg ) => {
                                    if ( previousLeg !== null && previousLeg.id === found.id ) {
                                        return null;
                                    }

                                    return found;
                                } );
                                onLegSelectedFromMapRef.current?.();
                            }
                        }
                    } else {
                        setSelectedLegRef.current( null );
                    }
                };
                handleMapClickRef.current = handleMapClick;
                map.on( "click", handleMapClick );

                const handleMapMouseMove = ( event: MapMouseEvent ): void => {
                    const lineLayerIds = resolveQueryableLineLayerIds();
                    if ( lineLayerIds.length === 0 ) {
                        map.getCanvas().style.cursor = "";

                        return;
                    }

                    // Cursor feedback is limited to clickable leg line layers.
                    const features = map.queryRenderedFeatures( event.point, { "layers": lineLayerIds } );
                    map.getCanvas().style.cursor = features.length > 0 ? "pointer" : "";
                };
                handleMapMouseMoveRef.current = handleMapMouseMove;
                map.on( "mousemove", handleMapMouseMove );
            }

            let drawnPolylineCount = 0;
            let drawnCoordinateCount = 0;

            if ( itinerary.legs.length > 0 ) {
                const polylines = itinerary.legs.
                    filter( ( legItem ) => legItem.geometry?.path !== undefined && legItem.mode !== undefined ).
                    map( ( legItem ) => {
                        const decodedPath = DecodePath( legItem.geometry?.path ?? "", legItem.geometry?.type, legItem.geometry?.precision );
                        const segmentId = Math.random().toString( 36 ).
                            substring( 7 );

                        const routeType = mapTransportModeToRouteType( legItem.mode );

                        return {
                            "id":          `${ legItem.mode }_${ segmentId }`,
                            "legId":       legItem.id,
                            "type":        routeType,
                            "coordinates": decodedPath.map( ( point ) => [ point.lng, point.lat ] as [number, number] ),
                        };
                    } );

                drawnPolylineCount = polylines.length;
                drawnCoordinateCount = polylines.reduce(
                    ( total, polyline ) => total + polyline.coordinates.length,
                    0,
                );

                if ( polylines.length > 0 ) {
                    setDrawnItineraryGeometry( {
                        "id": "transit_route",
                        polylines,
                    } );
                }

                recordActiveItineraryForMemoryProfiling( itinerary );
            } else {
                setDrawnItineraryGeometry( null );
                recordActiveItineraryForMemoryProfiling( null );
            }

            recordItineraryMapMemoryStats( {
                "legLayerCount":          legLayersByIdRef.current.size,
                drawnPolylineCount,
                drawnCoordinateCount,
                "activeItineraryBytes":   estimateJsonUtf8Bytes( itinerary ),
                "estimatedMapLayerBytes": estimateJsonUtf8Bytes( itinerary ) +
                  drawnCoordinateCount * 16,
            } );

            logAppMemoryReport( { "context": "itinerary-drawn" } );

            if ( bounds !== null ) {
                const fitBoundsOptions = {
                    "padding": {
                        "top":    topContainerHeight,
                        "bottom": bottomContainerHeight + 100,
                        "left":   0,
                        "right":  0,
                    },
                    "maxZoom":  15,
                    "duration": 1000,
                };

                const shouldAutoFit = lastAutoFitKeyRef.current !== itineraryFitKey;

                if ( shouldAutoFit ) {
                    map.fitBounds( bounds, fitBoundsOptions );

                    // Do not auto-fit again when only map style/layers are reloaded.
                    lastAutoFitKeyRef.current = itineraryFitKey;
                }

                setTimeout( () => {
                    map.resize();
                }, 300 );
            }

            applyItineraryLegLayerZOrder( map, legLayersByIdRef.current, selectedLegRef.current?.id );
        };

        addItineraryToMap();

        return () => {
            /*
             * Panel drag sets `isDragging` before this cleanup runs. Skipping teardown keeps
             * itinerary layers visible until the gesture ends (Effect A returns early while dragging).
             */
            if ( ephemeralStore.peek( itineraryPanelStateSlice ).isDragging ) {
                return;
            }

            removeMapInteractionHandlers();
            clearMapElements();
            legLayersByIdRef.current = new Map();
        };
    }, [ isDragging, itinerary, map, isMapReady, setDrawnItineraryGeometry, itineraryListHeight ] );

    /*
     * Effect B — only mutate visual emphasis and stop labels for current selection.
     * This avoids source/layer teardown when user is just selecting/deselecting legs.
     */
    useEffect( () => {
        if ( map === undefined || !isMapReady ) {
            return;
        }

        stopLabelControllerRef.current?.destroy();
        stopLabelControllerRef.current = null;

        const layersMap = legLayersByIdRef.current;
        if ( layersMap.size === 0 ) {
            return;
        }

        const currentItinerary = itineraryRef.current;
        const selectedId = selectedLeg?.id;

        // Resolve selected leg from current itinerary reference (safer than stale closure data).
        const selectedLegInItinerary =
            selectedId !== undefined && currentItinerary !== null
                ? currentItinerary.legs.find( ( l ) => l.id === selectedId )
                : undefined;

        layersMap.forEach( ( entry ) => {
            const { lineLayerId, legEndLayerId, walkDotsLayerId, leg } = entry;
            if ( map.getLayer( lineLayerId ) == null || map.getLayer( legEndLayerId ) == null ) {
                return;
            }

            // Emphasize selected leg and dim others without recreating map layers.
            const isSelected = selectedId !== undefined && leg.id === selectedId;
            const anySelected = selectedId !== undefined;
            const isWalkLeg = getTransportModeGroupForItineraryLeg( leg ) === TransportModeGroup.WALK;

            map.setPaintProperty(
                lineLayerId,
                "line-width",
                isSelected ? LINE_WIDTH_SELECTED : LINE_WIDTH_DEFAULT,
            );
            map.setPaintProperty(
                lineLayerId,
                "line-opacity",
                isWalkLeg ? 0 : anySelected && !isSelected ? LINE_OPACITY_DIMMED : LINE_OPACITY_DEFAULT,
            );

            if ( walkDotsLayerId !== undefined && map.getLayer( walkDotsLayerId ) != null ) {
                map.setLayoutProperty(
                    walkDotsLayerId,
                    "icon-size",
                    isSelected ? WALK_DOT_ICON_SIZE_SELECTED : WALK_DOT_ICON_SIZE_DEFAULT,
                );
                map.setPaintProperty(
                    walkDotsLayerId,
                    "icon-opacity",
                    anySelected && !isSelected ? LINE_OPACITY_DIMMED : LINE_OPACITY_DEFAULT,
                );
            }

            map.setPaintProperty(
                legEndLayerId,
                "circle-radius",
                isSelected ? CIRCLE_RADIUS_SELECTED : CIRCLE_RADIUS_DEFAULT,
            );
            map.setPaintProperty(
                legEndLayerId,
                "circle-opacity",
                anySelected && !isSelected ? LINE_OPACITY_DIMMED : LINE_OPACITY_DEFAULT,
            );
        } );

        applyItineraryLegLayerZOrder( map, layersMap, selectedId );

        if ( selectedLegInItinerary === undefined || currentItinerary === null ) {
            return;
        }

        const legColorHex = resolveBlackLikeHexToVehicleRouteGreen( getItineraryLegColorHex( selectedLegInItinerary ) );

        const stopLabelEntries = buildItineraryTransitPathStopCollisionEntries(
            selectedLegInItinerary,
            legColorHex,
            isDarkUi,
        );

        if ( stopLabelEntries.length > 0 ) {
            stopLabelControllerRef.current = new TransitPathStopCollisionController( map );
            stopLabelControllerRef.current.setEntries( stopLabelEntries );
        }

        return () => {
            stopLabelControllerRef.current?.destroy();
            stopLabelControllerRef.current = null;
        };
    }, [ map, isMapReady, isStopLinesReady, selectedLeg, itinerary, isDarkUi ] );
}
